import React, {useCallback, useMemo, useState} from 'react';
import {Text, useWindowDimensions, View} from 'react-native';
import {useSelector} from 'react-redux';

import Icon from '/component/base/Icon';
import RadioButtonGroup from '/component/base/RadioButton/RadioButtonGroup';
import {LeadsPoolTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';
import {selectLeadsPoolOptInRequirements} from '/redux/selector/remoteConfig.selector';

import {StaticFocusable} from '../ManageMySite/sections/StaticFocusable';
import {BottomSheetModal} from '../ManageMySite/widgets/BottomSheetModal';

import {useLeadsPool} from './LeadsPoolPopover.hook';
import {styles} from './LeadsPoolPopover.style';
import {
  LeadsPoolPopoverProps,
  QualificationSectionProps,
  RadioSectionProps,
} from './LeadsPoolPopover.types';

const MAX_FONT_SIZE_MULTIPLIER = 2;
const INFO_ICON_SIZE = 20;

const textScalingProps = {
  allowFontScaling: true,
  maxFontSizeMultiplier: MAX_FONT_SIZE_MULTIPLIER,
};

const TITLE_TEXT = String(tt('leadsPoolOption', 'Leads Pool Option'));
const QUAL_TITLE = String(
  tt('qualificationRequirements', 'Qualification Requirements'),
);
const QUAL_INTRO = String(
  tt(
    'leadsPoolQualificationIntro',
    'To qualify for the OPTAVIA leads pool each month:',
  ),
);
const RADIO_TITLE = String(
  tt(
    'chooseLeadPreference',
    'Choose whether you want to receive leads from the OPTAVIA leads pool:',
  ),
);
const YES_TEXT = String(tt('receiveCandidates', 'Receive candidates'));
const NO_TEXT = String(
  tt('doNotReceiveCandidates', 'Do not receive candidates'),
);
const YES_SUB = String(
  tt('receiveCandidatesSubText', 'I want to receive leads from the pool'),
);
const NO_SUB = String(
  tt('doNotReceiveCandidatesSubText', 'I do not want to receive leads'),
);
const WARNING_TEXT = String(
  tt(
    'leadsPoolWarningText',
    'If you choose to opt out of the leads pool, you will stop receiving leads within 24 hours. If opting back in, you must meet the leads pool assignment requirements and will begin receiving leads the first day of the following month.',
  ),
);

const DEFAULT_LEADS_POOL_REQUIREMENTS = {
  requirementOneText: 'Achieve **Executive Director** or higher',
  requirementTwoText:
    'Enroll at least **2 coach-converted Qualified New Clients**',
  qualifiedNewClientDefinitionText:
    'A Qualified New Client is a client who has never previously placed an order with OPTAVIA and has 275+ Personal Qualifying Volume (PQV) during a Monthly Bonus Period.',
};

const stripMarkdownBold = (value = '') => value.replace(/\*\*/g, '');

const getScaledIconSize = (fontScale: number): number =>
  Math.min(
    INFO_ICON_SIZE * fontScale,
    INFO_ICON_SIZE * MAX_FONT_SIZE_MULTIPLIER,
  );

const renderFormattedText = (
  text: string,
  testID?: string,
): React.ReactNode => {
  if (!text) {
    return null;
  }

  const parts = text.split(/\*\*(.*?)\*\*/g);

  return (
    <Text
      style={styles.bulletText}
      testID={testID}
      accessible={false}
      {...textScalingProps}>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <Text
            key={index}
            style={styles.bold}
            accessible={false}
            {...textScalingProps}>
            {part}
          </Text>
        ) : (
          part
        ),
      )}
    </Text>
  );
};

const QualificationSection = React.memo(
  ({
    requirementOneText,
    requirementTwoText,
    qualifiedNewClientDefinitionText,
  }: QualificationSectionProps) => {
    const {fontScale} = useWindowDimensions();
    const scaledInfoIconSize = getScaledIconSize(fontScale);

    return (
      <View
        style={styles.qualificationBox}
        testID={LeadsPoolTestIDs.qualificationContainer}
        accessible={false}>
        <StaticFocusable label={QUAL_TITLE} role="header">
          <View style={styles.qualificationTitleRow} accessible={false}>
            <Icon
              name="info"
              style={[
                styles.qualificationIcon,
                {
                  fontSize: scaledInfoIconSize,
                  width: scaledInfoIconSize,
                  height: scaledInfoIconSize,
                },
              ]}
              accessible={false}
              importantForAccessibility="no-hide-descendants"
            />

            <Text
              style={styles.qualificationTitle}
              accessible={false}
              testID={LeadsPoolTestIDs.qualificationTitle}
              {...textScalingProps}>
              {QUAL_TITLE}
            </Text>
          </View>
        </StaticFocusable>

        <StaticFocusable label={QUAL_INTRO} role="text">
          <Text
            style={styles.qualificationBody}
            accessible={false}
            testID={LeadsPoolTestIDs.qualificationIntro}
            {...textScalingProps}>
            {QUAL_INTRO}
          </Text>
        </StaticFocusable>

        <StaticFocusable
          label={stripMarkdownBold(requirementOneText)}
          role="text">
          <View style={styles.bulletRow} accessible={false}>
            <Text
              style={styles.bullet}
              accessible={false}
              {...textScalingProps}>
              {'\u2022'}
            </Text>

            {renderFormattedText(
              requirementOneText,
              LeadsPoolTestIDs.requirementOne,
            )}
          </View>
        </StaticFocusable>

        <StaticFocusable
          label={stripMarkdownBold(requirementTwoText)}
          role="text">
          <View style={styles.bulletRow} accessible={false}>
            <Text
              style={styles.bullet}
              accessible={false}
              {...textScalingProps}>
              {'\u2022'}
            </Text>

            {renderFormattedText(
              requirementTwoText,
              LeadsPoolTestIDs.requirementTwo,
            )}
          </View>
        </StaticFocusable>

        <StaticFocusable
          testID={LeadsPoolTestIDs.footnote}
          label={`Qualified New Client definition. ${stripMarkdownBold(
            qualifiedNewClientDefinitionText,
          )}`}
          role="text">
          <View accessible={false}>
            {renderFormattedText(qualifiedNewClientDefinitionText)}
          </View>
        </StaticFocusable>
      </View>
    );
  },
);

const RadioSection = React.memo(
  ({receiveLeads, onReceiveLeadsChange, isSaving}: RadioSectionProps) => {
    const radioOptions = useMemo(
      () => [
        {
          text: YES_TEXT,
          subText: YES_SUB,
          selected: receiveLeads === true,
          onPress: () => onReceiveLeadsChange(true),
          accessibilityLabel: YES_TEXT,
          testID: LeadsPoolTestIDs.radioYes,
        },
        {
          text: NO_TEXT,
          subText: NO_SUB,
          selected: receiveLeads === false,
          onPress: () => onReceiveLeadsChange(false),
          accessibilityLabel: NO_TEXT,
          testID: LeadsPoolTestIDs.radioNo,
        },
      ],
      [receiveLeads, onReceiveLeadsChange],
    );

    return (
      <View style={styles.radioWrapper} accessible={false}>
        <RadioButtonGroup
          allowFontScaling
          maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
          title={RADIO_TITLE}
          options={radioOptions}
          titleStyle={styles.questionText}
          subTextStyle={styles.radioSubLabel}
          isBold
          useAccessibleTitleWrapper
        />

        {isSaving && <View style={styles.radioOverlay} />}
      </View>
    );
  },
);

const WarningSection = React.memo(() => (
  <StaticFocusable
    testID={LeadsPoolTestIDs.warningContainer}
    label={`Leads pool timing warning. ${WARNING_TEXT}`}
    role="alert"
    style={styles.warningBox}>
    <Text style={styles.warningText} accessible={false} {...textScalingProps}>
      {WARNING_TEXT}
    </Text>
  </StaticFocusable>
));

export const LeadsPoolPopover = ({
  visible = true,
  onClose,
  onSave = async () => {},
  initialReceiveLeads = true,
  isSaving = false,
}: LeadsPoolPopoverProps) => {
  const requirements = useSelector(selectLeadsPoolOptInRequirements);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const saving = isSaving || isSubmitting;

  const finalRequirements = {
    requirementOneText:
      requirements?.requirementOneText ||
      DEFAULT_LEADS_POOL_REQUIREMENTS.requirementOneText,
    requirementTwoText:
      requirements?.requirementTwoText ||
      DEFAULT_LEADS_POOL_REQUIREMENTS.requirementTwoText,
    qualifiedNewClientDefinitionText:
      requirements?.qualifiedNewClientDefinitionText ||
      DEFAULT_LEADS_POOL_REQUIREMENTS.qualifiedNewClientDefinitionText,
  };

  const {receiveLeads, setReceiveLeads, isDirty, canSave, resetState} =
    useLeadsPool(initialReceiveLeads, visible);

  const handleClose = (): void => {
    if (saving) {
      return;
    }

    if (isDirty) {
      resetState();
    }

    onClose?.();
  };

  const handleSave = async (): Promise<void> => {
    if (saving || !isDirty || !canSave) {
      return;
    }

    try {
      setIsSubmitting(true);

      await onSave(receiveLeads);

      setTimeout(() => {
        onClose?.();
      }, 300);
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const handleReceiveLeadsChange = useCallback(
    (value: boolean): void => {
      if (saving) {
        return;
      }

      setReceiveLeads(value);
    },
    [saving, setReceiveLeads],
  );

  return (
    <BottomSheetModal
      visible={visible}
      title={TITLE_TEXT}
      onClose={handleClose}
      onClearAll={null}
      onSave={handleSave}
      scrollable
      canSave={isDirty && canSave && !saving}
      fullWidthSave
      isSaving={saving}>
      <QualificationSection {...finalRequirements} />

      <RadioSection
        receiveLeads={receiveLeads}
        onReceiveLeadsChange={handleReceiveLeadsChange}
        isSaving={saving}
      />

      <WarningSection />
    </BottomSheetModal>
  );
};
